import React, { useEffect, useState } from 'react';
import { useChatUi } from '../ChatContext';

function withRetry(src, attempt) {
  if (!attempt || !src || src.startsWith('data:')) return src;
  return `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}`;
}

/**
 * A page image in a fixed aspect-ratio box: no layout shift, lazy-loaded (nothing is fetched until it is
 * near the viewport), a shimmer while loading, a fade-in when ready and a retry button when it fails.
 */
export default function PageImage({ src, alt, ratio = '3 / 4', className = '', fit = 'cover', eager = false }) {
  const { t } = useChatUi();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState(src ? 'loading' : 'error');

  useEffect(() => {
    setAttempt(0);
    setState(src ? 'loading' : 'error');
  }, [src]);

  const retry = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAttempt((n) => n + 1);
    setState('loading');
  };

  return (
    <span className={`cv-img ${className}`} style={{ aspectRatio: ratio }} data-state={state}>
      {state !== 'error' && (
        <img
          key={attempt}
          src={withRetry(src, attempt)}
          alt={alt || ''}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          draggable={false}
          style={{ objectFit: fit }}
          onLoad={() => setState('loaded')}
          onError={() => setState('error')}
        />
      )}
      {state === 'loading' && <span className="cv-img__shimmer" aria-hidden="true" />}
      {state === 'error' && (
        <span className="cv-img__error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 16l5-5 4 4 3-3 6 6" />
            <path d="M4 4l16 16" />
          </svg>
          {src && (
            <span
              role="button"
              tabIndex={0}
              className="cv-img__retry"
              onClick={retry}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') retry(e); }}
            >
              {t('Retry')}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
